/**
 * ETL Pipeline Dashboard
 *
 * Admin interface for managing Provider Directory ETL imports
 * Design: Tailwind + Lucide icons (matching Provider Import Manager)
 */

import React, { useState, useEffect, useCallback } from 'react';
import SchedulerManager from './SchedulerManager';
import {
  Play,
  RefreshCw,
  Plus,
  Trash2,
  Edit,
  Calendar,
  Database,
  CheckCircle,
  XCircle,
  Clock,
  RotateCw,
  Activity,
  FileText,
  Layers
} from 'lucide-react';
import { config } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
// Use proxy path in production to avoid CORS issues
// The proxy /provider-api/* maps to https://provider-directory-api.onrender.com/api/*
const getProviderApiUrl = () => {
  // Desktop (Tauri) has no Vercel proxy, so the relative /provider-api path
  // won't resolve — hit the absolute service URL directly, same as dev.
  if (config.isProduction && !config.isDesktop) {
    // Don't add /api here - the proxy already maps to /api
    return '/provider-api';
  }
  // For dev/local/desktop, use the full base URL which includes /api
  return config.providerApi.baseUrl;
};

const PROVIDER_API_URL = getProviderApiUrl();

interface Job {
  id: number | string;  // string for cron runs (e.g. "geo-5", "t2-10")
  source_name: string;
  job_type: string;
  status: string;
  staged_records: number;
  transformed_records: number;
  validated_records: number;
  loaded_records: number;
  failed_records: number;
  duplicate_records: number;
  geocoding_success_rate: number;
  avg_quality_score: number;
  error_details: any;
  started_at: string;
  completed_at: string;
  created_at: string;
  duration_seconds: number;
}

interface Schedule {
  id: number;
  source_name: string;
  schedule_name: string;
  frequency: string;
  cron_expression: string;
  job_type: string;
  is_active: boolean;
  last_run_at: string;
  next_run_at: string;
  auto_approve_threshold: number;
}

interface DataSource {
  id: number;
  source_name: string;
  source_type: string;
  base_url: string;
  is_active: boolean;
  refresh_frequency: string;
  total_jobs: number;
  successful_jobs: number;
  total_loaded: number;
  last_import: string;
}

// Tab component
const Tab = ({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
      active
        ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`}
  >
    {icon}
    {label}
  </button>
);

// Stat Card component
const StatCard = ({ label, value, icon, color = 'blue' }: { label: string; value: string | number; icon: React.ReactNode; color?: string }) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      <div className={`inline-flex p-2 rounded-lg mb-2 ${colorClasses[color]}`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
};

// Status Badge component
const StatusBadge = ({ status }: { status: string }) => {
  const statusStyles: Record<string, string> = {
    COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    RUNNING: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  };
  const statusIcons: Record<string, React.ReactNode> = {
    COMPLETED: <CheckCircle className="h-3 w-3" />,
    FAILED: <XCircle className="h-3 w-3" />,
    RUNNING: <Activity className="h-3 w-3 animate-pulse" />,
    PENDING: <Clock className="h-3 w-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {statusIcons[status]}
      {status}
    </span>
  );
};

export default function ETLDashboard() {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [activeTab, setActiveTab] = useState(0);
  const [expandedJobId, setExpandedJobId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Jobs state
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsSummary, setJobsSummary] = useState<any>({});

  // Schedules state
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

  // Data sources state
  const [sources, setSources] = useState<DataSource[]>([]);

  // New import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importForm, setImportForm] = useState({
    source_name: 'IRS_Bulk',
    job_type: 'INCREMENTAL',
    limit: '',
    filter_states: [] as string[],
  });

  // Schedule form
  const [scheduleForm, setScheduleForm] = useState({
    source_name: 'IRS_Bulk',
    schedule_name: '',
    frequency: 'WEEKLY',
    cron_expression: '',
    job_type: 'INCREMENTAL',
    auto_approve_threshold: 0.8,
  });

  // Fetch functions
  const fetchJobs = useCallback(async () => {
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/jobs?limit=50`);
      const data = await response.json();
      setJobs(data.jobs || []);
      setJobsSummary(data.summary || {});
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/schedules`);
      const data = await response.json();
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    }
  }, []);

  const fetchSources = useCallback(async () => {
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/sources`);
      const data = await response.json();
      setSources(data.sources || []);
    } catch (err) {
      console.error('Failed to fetch sources:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchJobs(), fetchSchedules(), fetchSources()])
      .finally(() => setLoading(false));
  }, [fetchJobs, fetchSchedules, fetchSources]);

  // Handlers
  const handleRefresh = () => {
    setLoading(true);
    Promise.all([fetchJobs(), fetchSchedules(), fetchSources()])
      .finally(() => setLoading(false));
  };

  const handleDeleteJob = async (jobId: number | string) => {
    if (!confirm(`Delete job ${jobId} and all its staging records?`)) return;
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/jobs/${jobId}`, { method: 'DELETE' });
      if (response.ok) fetchJobs();
      else setError('Failed to delete job');
    } catch (err) {
      setError('Failed to delete job');
    }
  };

  const handleRerunJob = async (jobId: number | string) => {
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/jobs/${jobId}/rerun`, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        alert(`Created new job ${data.new_job_id}`);
        fetchJobs();
      } else setError(data.detail || 'Failed to rerun job');
    } catch (err) {
      setError('Failed to rerun job');
    }
  };

  const handleTriggerImport = async () => {
    try {
      const params = new URLSearchParams({
        source_name: importForm.source_name,
        job_type: importForm.job_type,
      });
      if (importForm.limit) params.append('limit', importForm.limit);
      if (importForm.filter_states.length > 0) params.append('filter_states', importForm.filter_states.join(','));

      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/run?${params}`, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        alert(`Created job ${data.job_id}: ${data.message}`);
        setShowImportDialog(false);
        fetchJobs();
      } else setError(data.detail || 'Failed to trigger import');
    } catch (err) {
      setError('Failed to trigger import');
    }
  };

  const handleToggleSchedule = async (schedule: Schedule) => {
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/schedules/${schedule.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !schedule.is_active }),
      });
      if (response.ok) fetchSchedules();
    } catch (err) {
      setError('Failed to update schedule');
    }
  };

  const handleSaveSchedule = async () => {
    try {
      const url = editingSchedule
        ? `${PROVIDER_API_URL}/admin/etl/schedules/${editingSchedule.id}`
        : `${PROVIDER_API_URL}/admin/etl/schedules`;
      const response = await fetch(url, {
        method: editingSchedule ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleForm),
      });
      if (response.ok) {
        setShowScheduleDialog(false);
        setEditingSchedule(null);
        fetchSchedules();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save schedule');
      }
    } catch (err) {
      setError('Failed to save schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!confirm('Delete this schedule?')) return;
    try {
      const response = await fetch(`${PROVIDER_API_URL}/admin/etl/schedules/${scheduleId}`, { method: 'DELETE' });
      if (response.ok) fetchSchedules();
    } catch (err) {
      setError('Failed to delete schedule');
    }
  };

  // Utility functions
  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return fmt.dateTime(dateStr);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Layers className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.etl_pipeline_dashboard', 'ETL Pipeline Dashboard')}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('admin.manage_provider_imports_schedules_and', 'Manage provider imports, schedules, and geocoding')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImportDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t('admin.new_import', 'New Import')}
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 dark:border-gray-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label={t('admin.total_jobs', 'Total Jobs')} value={jobsSummary.total_jobs || 0} icon={<FileText className="h-5 w-5" />} color="blue" />
            <StatCard label={t('admin.completed', 'Completed')} value={jobsSummary.completed || 0} icon={<CheckCircle className="h-5 w-5" />} color="green" />
            <StatCard label={t('admin.failed_1', 'Failed')} value={jobsSummary.failed || 0} icon={<XCircle className="h-5 w-5" />} color="red" />
            <StatCard label={t('admin.total_loaded', 'Total Loaded')} value={(jobsSummary.total_loaded || 0).toLocaleString()} icon={<Database className="h-5 w-5" />} color="blue" />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
              <div className="flex w-max min-w-full">
                <Tab label={t('admin.import_jobs', 'Import Jobs')} icon={<FileText className="h-4 w-4" />} active={activeTab === 0} onClick={() => setActiveTab(0)} />
                <Tab label={t('admin.schedules', 'Schedules')} icon={<Calendar className="h-4 w-4" />} active={activeTab === 1} onClick={() => setActiveTab(1)} />
                <Tab label={t('admin.data_sources', 'Data Sources')} icon={<Database className="h-4 w-4" />} active={activeTab === 2} onClick={() => setActiveTab(2)} />
                <Tab label={t('admin.scheduler', 'Scheduler')} icon={<Calendar className="h-4 w-4" />} active={activeTab === 3} onClick={() => setActiveTab(3)} />
              </div>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Jobs Tab */}
          {!loading && activeTab === 0 && (
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">ID</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.source', 'Source')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.type', 'Type')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.status', 'Status')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.staged', 'Staged')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.loaded', 'Loaded')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.failed', 'Failed')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.geocode', 'Geocode %')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.duration', 'Duration')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.created', 'Created')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <React.Fragment key={job.id}>
                    <tr
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                    >
                      <td className="py-3 px-2 text-gray-900 dark:text-gray-100">{job.id}</td>
                      <td className="py-3 px-2 text-gray-900 dark:text-gray-100">{job.source_name}</td>
                      <td className="py-3 px-2">
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">{job.job_type}</span>
                      </td>
                      <td className="py-3 px-2"><StatusBadge status={job.status} /></td>
                      <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">{job.staged_records?.toLocaleString() || '-'}</td>
                      <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">{job.loaded_records?.toLocaleString() || '-'}</td>
                      <td className="py-3 px-2 text-right">
                        {job.failed_records > 0 ? (
                          <span className="text-red-600 dark:text-red-400">{job.failed_records}</span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">
                        {job.geocoding_success_rate ? `${job.geocoding_success_rate.toFixed(1)}%` : '-'}
                      </td>
                      <td className="py-3 px-2 text-gray-600 dark:text-gray-400">{formatDuration(job.duration_seconds)}</td>
                      <td className="py-3 px-2 text-gray-600 dark:text-gray-400 text-xs">{formatDate(job.created_at)}</td>
                      <td className="py-3 px-2">
                        {/* Cron runs (geocoder / tier 2 enrichment) live in their
                            own tables and are managed on a schedule — no rerun/delete. */}
                        {typeof job.id === 'string' && (job.id.startsWith('geo-') || job.id.startsWith('t2-')) ? (
                          <span className="text-xs text-gray-400" title="Scheduled cron run">cron</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button onClick={(e) => { e.stopPropagation(); handleRerunJob(job.id); }} className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400" title={t('admin.rerun', 'Rerun')}>
                              <RotateCw className="h-4 w-4" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }} className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400" title={t('admin.delete', 'Delete')}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {expandedJobId === job.id && (
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        <td colSpan={11} className="px-4 py-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
                            <div><div className="text-gray-500">Staged</div><div className="font-medium">{job.staged_records?.toLocaleString() ?? '—'}</div></div>
                            <div><div className="text-gray-500">Transformed</div><div className="font-medium">{job.transformed_records?.toLocaleString() ?? '—'}</div></div>
                            <div><div className="text-gray-500">Validated</div><div className="font-medium">{job.validated_records?.toLocaleString() ?? '—'}</div></div>
                            <div><div className="text-gray-500">Loaded</div><div className="font-medium text-green-600 dark:text-green-400">{job.loaded_records?.toLocaleString() ?? '—'}</div></div>
                            <div><div className="text-gray-500">Failed</div><div className="font-medium text-red-600 dark:text-red-400">{job.failed_records?.toLocaleString() ?? '—'}</div></div>
                            <div><div className="text-gray-500">Duplicates</div><div className="font-medium">{job.duplicate_records?.toLocaleString() ?? '—'}</div></div>
                            <div><div className="text-gray-500">Geocode %</div><div className="font-medium">{job.geocoding_success_rate != null ? `${job.geocoding_success_rate.toFixed(1)}%` : '—'}</div></div>
                            <div><div className="text-gray-500">Avg quality</div><div className="font-medium">{job.avg_quality_score != null ? job.avg_quality_score.toFixed(2) : '—'}</div></div>
                            <div><div className="text-gray-500">Started</div><div className="font-medium">{formatDate(job.started_at)}</div></div>
                            <div><div className="text-gray-500">Completed</div><div className="font-medium">{formatDate(job.completed_at)}</div></div>
                            <div><div className="text-gray-500">Duration</div><div className="font-medium">{formatDuration(job.duration_seconds)}</div></div>
                          </div>
                          {job.error_details && (
                            <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-300 font-mono whitespace-pre-wrap break-all">
                              {typeof job.error_details === 'string' ? job.error_details : JSON.stringify(job.error_details)}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                  {jobs.length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-8 text-center text-gray-500 dark:text-gray-400">{t('admin.no_import_jobs_found', 'No import jobs found')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Schedules Tab */}
          {!loading && activeTab === 1 && (
            <div className="p-4">
              <div className="mb-4">
                <button
                  onClick={() => {
                    setEditingSchedule(null);
                    setScheduleForm({ source_name: 'IRS_Bulk', schedule_name: '', frequency: 'WEEKLY', cron_expression: '', job_type: 'INCREMENTAL', auto_approve_threshold: 0.8 });
                    setShowScheduleDialog(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  {t('admin.add_schedule', 'Add Schedule')}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.active', 'Active')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.name', 'Name')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.source_1', 'Source')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.frequency', 'Frequency')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.job_type', 'Job Type')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.autoapprove', 'Auto-Approve')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.last_run', 'Last Run')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.next_run', 'Next Run')}</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.actions_1', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((schedule) => (
                      <tr key={schedule.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-3 px-2">
                          <button
                            onClick={() => handleToggleSchedule(schedule)}
                            className={`w-10 h-5 rounded-full transition-colors ${schedule.is_active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                          >
                            <div className={`w-4 h-4 bg-white dark:bg-gray-800 rounded-full shadow transition-transform ${schedule.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </td>
                        <td className="py-3 px-2 text-gray-900 dark:text-gray-100">{schedule.schedule_name}</td>
                        <td className="py-3 px-2 text-gray-900 dark:text-gray-100">{schedule.source_name}</td>
                        <td className="py-3 px-2">
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-xs">{schedule.frequency}</span>
                        </td>
                        <td className="py-3 px-2 text-gray-600 dark:text-gray-400">{schedule.job_type}</td>
                        <td className="py-3 px-2 text-gray-600 dark:text-gray-400">{(schedule.auto_approve_threshold * 100).toFixed(0)}%</td>
                        <td className="py-3 px-2 text-gray-600 dark:text-gray-400 text-xs">{formatDate(schedule.last_run_at)}</td>
                        <td className="py-3 px-2 text-gray-600 dark:text-gray-400 text-xs">{formatDate(schedule.next_run_at)}</td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingSchedule(schedule);
                                setScheduleForm({
                                  source_name: schedule.source_name,
                                  schedule_name: schedule.schedule_name,
                                  frequency: schedule.frequency,
                                  cron_expression: schedule.cron_expression || '',
                                  job_type: schedule.job_type,
                                  auto_approve_threshold: schedule.auto_approve_threshold,
                                });
                                setShowScheduleDialog(true);
                              }}
                              className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400"
                              title={t('admin.edit', 'Edit')}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDeleteSchedule(schedule.id)} className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400" title={t('admin.delete_1', 'Delete')}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {schedules.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-400">{t('admin.no_schedules_configured', 'No schedules configured')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Data Sources Tab */}
          {!loading && activeTab === 2 && (
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.source_2', 'Source')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.type_1', 'Type')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.refresh', 'Refresh')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.status_1', 'Status')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.jobs', 'Jobs')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.successful', 'Successful')}</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.loaded_1', 'Loaded')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.last_import', 'Last Import')}</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">{t('admin.actions_2', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((source) => (
                    <tr key={source.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-2 font-medium text-gray-900 dark:text-gray-100">{source.source_name}</td>
                      <td className="py-3 px-2">
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">{source.source_type}</span>
                      </td>
                      <td className="py-3 px-2 text-gray-600 dark:text-gray-400">{source.refresh_frequency}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs ${source.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600'}`}>
                          {source.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">{source.total_jobs}</td>
                      <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">{source.successful_jobs}</td>
                      <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">{(source.total_loaded || 0).toLocaleString()}</td>
                      <td className="py-3 px-2 text-gray-600 dark:text-gray-400 text-xs">{formatDate(source.last_import)}</td>
                      <td className="py-3 px-2">
                        <button
                          onClick={() => { setImportForm({ ...importForm, source_name: source.source_name }); setShowImportDialog(true); }}
                          className="p-1 text-gray-500 hover:text-green-600 dark:text-gray-400"
                          title={t('admin.run_import', 'Run Import')}
                        >
                          <Play className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Scheduler Tab — DB-backed unified cron manager */}
          {!loading && activeTab === 3 && (
            <div className="p-4">
              <SchedulerManager />
            </div>
          )}
        </div>

        {/* Import Dialog */}
        {showImportDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">{t('admin.new_import_job', 'New Import Job')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.data_source', 'Data Source')}</label>
                  <select
                    value={importForm.source_name}
                    onChange={(e) => setImportForm({ ...importForm, source_name: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <optgroup label="CBO Import">
                      <option value="IRS_Bulk">IRS Bulk — Nonprofits</option>
                      <option value="IRS_Form990">Form 990 XML — Phone, Website, Mission</option>
                    </optgroup>
                    <optgroup label="Clinical Provider Import">
                      <option value="NPPES">CMS NPPES — NPI-1 & NPI-2</option>
                      <option value="HRSA">HRSA — Health Centers</option>
                    </optgroup>
                    <optgroup label="Enrichment">
                      <option value="contact_waterfall">Contact Waterfall — Brave + Hunter</option>
                      <option value="geocoding">Geocoding — Census API</option>
                      <option value="ai_enrichment">AI Enrichment — Claude AI</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.job_type_1', 'Job Type')}</label>
                  <select
                    value={importForm.job_type}
                    onChange={(e) => setImportForm({ ...importForm, job_type: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="INCREMENTAL">{t('admin.incremental', 'Incremental')}</option>
                    <option value="FULL">{t('admin.full_refresh', 'Full Refresh')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.limit_optional', 'Limit (optional)')}</label>
                  <input
                    type="number"
                    value={importForm.limit}
                    onChange={(e) => setImportForm({ ...importForm, limit: e.target.value })}
                    placeholder={t('admin.no_limit', 'No limit')}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setShowImportDialog(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">{t('admin.cancel', 'Cancel')}</button>
                <button onClick={handleTriggerImport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{t('admin.start_import', 'Start Import')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Dialog */}
        {showScheduleDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">{editingSchedule ? 'Edit Schedule' : 'New Schedule'}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.schedule_name', 'Schedule Name')}</label>
                  <input
                    type="text"
                    value={scheduleForm.schedule_name}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_name: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.data_source_1', 'Data Source')}</label>
                  <select
                    value={scheduleForm.source_name}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, source_name: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <optgroup label="CBO Import">
                      <option value="IRS_Bulk">IRS Bulk — Nonprofits (all regions)</option>
                      <option value="IRS_Form990">Form 990 XML — Phone, Website, Mission (free)</option>
                    </optgroup>
                    <optgroup label="Clinical Provider Import">
                      <option value="NPPES">CMS NPPES — NPI-1 & NPI-2 Providers</option>
                      <option value="HRSA">HRSA — Federally Qualified Health Centers</option>
                    </optgroup>
                    <optgroup label="Enrichment">
                      <option value="contact_waterfall">Contact Waterfall — Brave Search + Hunter.io</option>
                      <option value="geocoding">Geocoding — Census Bureau API (free)</option>
                      <option value="ai_enrichment">AI Enrichment — Claude AI analysis</option>
                      <option value="ntee_enrichment">NTEE Services — Deterministic (free)</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.frequency_1', 'Frequency')}</label>
                  <select
                    value={scheduleForm.frequency}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  >
                    <option value="DAILY">{t('admin.daily', 'Daily')}</option>
                    <option value="WEEKLY">{t('admin.weekly', 'Weekly')}</option>
                    <option value="MONTHLY">{t('admin.monthly', 'Monthly')}</option>
                    <option value="CUSTOM">{t('admin.custom_cron', 'Custom (Cron)')}</option>
                  </select>
                </div>
                {scheduleForm.frequency === 'CUSTOM' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.cron_expression', 'Cron Expression')}</label>
                    <input
                      type="text"
                      value={scheduleForm.cron_expression}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, cron_expression: e.target.value })}
                      placeholder="0 2 * * 0"
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  </div>
                )}
                {/* Enrichment-specific config — show when enrichment source selected */}
                {['contact_waterfall', 'geocoding', 'ai_enrichment', 'IRS_Form990'].includes(scheduleForm.source_name) && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-3">
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300">Enrichment Settings</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-700 dark:text-gray-300 mb-1">Batch Limit</label>
                        <select
                          value={(scheduleForm as any).batch_limit || '10000'}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, batch_limit: e.target.value } as any)}
                          className="w-full px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm"
                        >
                          <option value="1000">1,000</option>
                          <option value="5000">5,000</option>
                          <option value="10000">10,000</option>
                          <option value="25000">25,000</option>
                          <option value="50000">50,000</option>
                        </select>
                      </div>
                      {scheduleForm.source_name === 'contact_waterfall' && (
                        <div>
                          <label className="block text-xs text-gray-700 dark:text-gray-300 mb-1">Budget per run ($)</label>
                          <input
                            type="number"
                            value={(scheduleForm as any).budget || 50}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, budget: parseFloat(e.target.value) } as any)}
                            className="w-full px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm"
                          />
                        </div>
                      )}
                      {scheduleForm.source_name === 'IRS_Form990' && (
                        <div>
                          <label className="block text-xs text-gray-700 dark:text-gray-300 mb-1">Filing Year</label>
                          <select
                            value={(scheduleForm as any).year || '2024'}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, year: e.target.value } as any)}
                            className="w-full px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm"
                          >
                            <option value="2024">2024</option>
                            <option value="2023">2023</option>
                            <option value="2022">2022</option>
                            <option value="2021">2021</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('admin.autoapprove_threshold', 'Auto-Approve Threshold')}</label>
                  <input
                    type="number"
                    value={scheduleForm.auto_approve_threshold}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, auto_approve_threshold: parseFloat(e.target.value) || 0.8 })}
                    step={0.1}
                    min={0}
                    max={1}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setShowScheduleDialog(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">{t('admin.cancel_1', 'Cancel')}</button>
                <button onClick={handleSaveSchedule} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{editingSchedule ? 'Update' : 'Create'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
